import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, RefreshCw, FileText, Sparkles, Eye, Send, ShieldCheck,
  Building2, Briefcase, FileSearch, BarChart3, Library, Plug, Download,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import RegistryPanel from './RegistryPanel';

/**
 * M5 — MRV Reporting
 *
 * Mirrors saarv-mrv-beta's MRV Reports page:
 *   - Top dark panel with 3 AI-generated PDF cards
 *     (Asset Owner / Auditor Verification / Registry Submission)
 *   - 4 quick-generate report cards
 *   - List of existing reports
 *   - Sub-tab for Registry submissions
 */

const SUB_TABS = [
  { id: 'reports',    label: 'Reports',      Icon: FileText },
  { id: 'generate',   label: 'Generate Report', Icon: Plus },
  { id: 'registry',   label: 'Registry',     Icon: Send },
];

const AI_VARIANTS = [
  {
    id: 'asset_owner',
    title: 'Asset Owner Report',
    description: 'Emissions footprint, reduction opportunities & carbon revenue potential',
    audience: 'Company management / CFO',
    Icon: Briefcase,
    iconBg: 'bg-blue-600',
  },
  {
    id: 'auditor_verification',
    title: 'Auditor Verification Report',
    description: 'Methodology compliance, data traceability & calculation verification',
    audience: 'Third-party verifiers',
    Icon: ShieldCheck,
    iconBg: 'bg-emerald-600',
  },
  {
    id: 'registry_submission',
    title: 'Registry Submission Report',
    description: 'Methodology-compliant monitoring report for credit issuance',
    audience: 'Verra / Gold Standard / CCTS',
    Icon: Send,
    iconBg: 'bg-violet-600',
  },
];

const QUICK_VARIANTS = [
  {
    id: 'executive_summary',
    label: 'Executive Summary',
    badge: 'executive',
    badgeColor: 'bg-amber-100 text-amber-700',
    description: 'High-level CO2e overview with scope breakdown',
    Icon: BarChart3,
  },
  {
    id: 'detailed_inventory',
    label: 'Detailed Emissions',
    badge: 'detailed',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    description: 'Full emissions data by type and source',
    Icon: FileSearch,
  },
  {
    id: 'scope_breakdown',
    label: 'Facility Breakdown',
    badge: 'facility',
    badgeColor: 'bg-violet-100 text-violet-700',
    description: 'Per-facility emissions analysis',
    Icon: Building2,
  },
  {
    id: 'compliance',
    label: 'Compliance Report',
    badge: 'compliance',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    description: 'GHG Protocol / ISO 14064 compliance',
    Icon: Library,
  },
];

// Decode a base64 PDF payload and trigger a browser download.
function downloadBase64Pdf(b64, filename) {
  if (!b64) return false;
  try {
    const byteChars = atob(b64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'mrv_report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (e) {
    console.error('PDF download failed:', e);
    return false;
  }
}

export default function ReportsPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [activeSubTab, setActiveSubTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [aiVariantInProgress, setAiVariantInProgress] = useState(null);
  const [aiVariantResult, setAiVariantResult] = useState(null);
  const [quickInProgress, setQuickInProgress] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mrv/reports`);
      setReports(res.data.reports || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const view = async (report_id) => {
    try {
      const res = await axios.get(`${API}/mrv/reports/${report_id}`);
      setViewing(res.data);
      setAiNarrative(null);
    } catch (e) {
      showError(e.response?.data?.detail || 'Load report failed');
    }
  };

  const generateNarrative = async (report_id) => {
    try {
      showInfo('Generating AI narrative…');
      const res = await axios.post(`${API}/mrv/reports/generate-ai-report`, { report_id });
      setAiNarrative(res.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'AI narrative failed');
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/mrv/reports/${id}`);
      showSuccess('Report deleted');
      if (viewing?.id === id) setViewing(null);
      fetchReports();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const generateAiVariant = async (variant) => {
    setAiVariantInProgress(variant);
    setAiVariantResult(null);
    try {
      const res = await axios.post(`${API}/mrv/reports/ai/${variant}`, {});
      setAiVariantResult({ variant, payload: res.data });
      // Trigger PDF download immediately if the backend produced one.
      const downloaded = downloadBase64Pdf(res.data?.pdf_base64, res.data?.filename);
      showSuccess(downloaded
        ? `AI ${variant.replace('_', ' ')} report ready — PDF downloaded & saved to Reports`
        : `AI ${variant.replace('_', ' ')} report saved to Reports`);
      await fetchReports();
      setActiveSubTab('reports');
    } catch (e) {
      showError(e.response?.data?.detail || 'AI report failed');
    } finally {
      setAiVariantInProgress(null);
    }
  };

  // Download a saved report's PDF on demand. Backend re-renders from the
  // stored report_data so it always reflects the report at generation time.
  const downloadReportPdf = async (report) => {
    try {
      const res = await axios.get(`${API}/mrv/reports/${report.id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      // Build a sensible filename from the report's title.
      const safe = (report.title || 'mrv_report').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      link.href = url;
      link.download = `${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showSuccess('PDF downloaded');
    } catch (e) {
      const msg = e.response?.data instanceof Blob
        ? 'PDF download failed'
        : (e.response?.data?.detail || 'PDF download failed');
      showError(msg);
    }
  };

  const quickGenerate = async (variant) => {
    setQuickInProgress(variant);
    try {
      const today = new Date();
      const start = new Date(today);
      start.setMonth(today.getMonth() - 1);
      await axios.post(`${API}/mrv/reports`, {
        report_type: variant,
        title: QUICK_VARIANTS.find(q => q.id === variant)?.label || variant,
        period_start: start.toISOString().slice(0, 10),
        period_end: today.toISOString().slice(0, 10),
        site_ids: siteId ? [siteId] : [],
      });
      showSuccess('Report generated — see Reports tab');
      await fetchReports();
      setActiveSubTab('reports');
    } catch (e) {
      showError(e.response?.data?.detail || 'Generate failed');
    } finally {
      setQuickInProgress(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">MRV Reports</h2>
          <div className="text-xs text-gray-500">M5 — Generate GHG Protocol & ISO 14064 compliant reports</div>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchReports} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex gap-2">
          {SUB_TABS.map(({ id, label, Icon }) => (
            <button key={id} type="button" onClick={() => setActiveSubTab(id)}
                    className={`py-2 px-3 inline-flex items-center gap-1 border-b-2 text-sm font-medium ${
                      activeSubTab === id
                        ? 'border-emerald-600 text-emerald-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'registry' && <RegistryPanel />}

      {activeSubTab === 'generate' && (
        <>
          {/* AI-Generated dark panel */}
          <div className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 mb-6">
            <div className="flex items-center gap-2 mb-1 text-white">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <h3 className="font-semibold text-lg">AI-Generated MRV Reports</h3>
            </div>
            <p className="text-sm text-slate-400 mb-5">
              Professional PDF reports powered by GPT-4o with Saarv branding
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {AI_VARIANTS.map(v => (
                <AiCard
                  key={v.id}
                  variant={v}
                  isActive={aiVariantInProgress === v.id}
                  onGenerate={() => generateAiVariant(v.id)}
                />
              ))}
            </div>
          </div>

          {/* AI variant output */}
          {aiVariantResult && (
            <AiResultViewer
              result={aiVariantResult}
              onClose={() => setAiVariantResult(null)}
            />
          )}

          {/* Quick generate cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_VARIANTS.map(q => (
              <QuickCard
                key={q.id}
                variant={q}
                inProgress={quickInProgress === q.id}
                onGenerate={() => quickGenerate(q.id)}
              />
            ))}
          </div>
        </>
      )}

      {activeSubTab === 'reports' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-gray-700">Title</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Period</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">{loading ? 'Loading…' : 'No reports yet — generate one in the Generate Report tab'}</td></tr>
                )}
                {reports.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{r.title}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.report_type}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.period_start} → {r.period_end}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        r.status === 'published' ? 'bg-emerald-100 text-emerald-700'
                        : r.status === 'generated' ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 flex gap-1">
                      <button onClick={() => view(r.id)} className="text-blue-600 hover:text-blue-800 p-1" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadReportPdf(r)} className="text-emerald-600 hover:text-emerald-800 p-1" title="Download PDF">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteReport(r.id)} className="text-red-500 hover:text-red-700 p-1" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-gray-200 rounded-md p-4 bg-gray-50 min-h-[300px]">
            {!viewing ? (
              <div className="text-center text-gray-500 text-sm py-12">
                <FileText className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                Select a report to view its data
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{viewing.title}</h3>
                    <div className="text-xs text-gray-600">
                      {viewing.period_start} → {viewing.period_end} · {viewing.report_type}
                    </div>
                  </div>
                  <button onClick={() => generateNarrative(viewing.id)}
                          className="px-2 py-1 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded inline-flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> AI Narrative
                  </button>
                </div>
                <ReportSummaryView report_data={viewing.report_data} />
                {aiNarrative && (
                  <div className="mt-4 p-3 bg-violet-50 border border-violet-200 rounded">
                    <div className="text-sm font-semibold text-violet-900 mb-2 flex items-center gap-1">
                      <Sparkles className="w-4 h-4" /> AI Narrative
                    </div>
                    {aiNarrative.executive_summary && (
                      <div className="mb-2"><strong>Executive summary:</strong> {aiNarrative.executive_summary}</div>
                    )}
                    {aiNarrative.key_findings?.length > 0 && (
                      <div className="mb-2">
                        <strong>Key findings:</strong>
                        <ul className="list-disc list-inside text-sm">
                          {aiNarrative.key_findings.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}
                    {aiNarrative.recommendations?.length > 0 && (
                      <div>
                        <strong>Recommendations:</strong>
                        <ul className="list-disc list-inside text-sm">
                          {aiNarrative.recommendations.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AiCard({ variant, isActive, onGenerate }) {
  const Icon = variant.Icon;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-4 hover:bg-slate-800/70 transition-colors">
      <div className={`w-10 h-10 rounded-lg ${variant.iconBg} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h4 className="font-semibold text-white text-base mb-1">{variant.title}</h4>
      <p className="text-xs text-slate-400 mb-3">{variant.description}</p>
      <div className="text-xs text-slate-500 mb-3">
        <span className="text-slate-400 font-medium">Audience:</span> {variant.audience}
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={isActive}
        className="w-full py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50"
      >
        <Sparkles className="w-4 h-4" />
        {isActive ? 'Generating…' : 'Generate PDF'}
      </button>
    </div>
  );
}

function QuickCard({ variant, inProgress, onGenerate }) {
  const Icon = variant.Icon;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col">
      <div className="flex items-start justify-between mb-2">
        <FileText className="w-5 h-5 text-gray-500" />
        <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${variant.badgeColor}`}>
          {variant.badge}
        </span>
      </div>
      <h4 className="font-semibold text-gray-900 text-sm mb-1">{variant.label}</h4>
      <p className="text-xs text-gray-500 mb-3 flex-1">{variant.description}</p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={inProgress}
        className="py-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        {inProgress ? 'Generating…' : 'Quick Generate'}
      </button>
    </div>
  );
}

function AiResultViewer({ result, onClose }) {
  const p = result.payload || {};
  const reDownload = () => downloadBase64Pdf(p.pdf_base64, p.filename);
  return (
    <div className="mb-6 p-4 border border-violet-300 bg-violet-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-violet-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> {p.title || 'AI report'}
        </h3>
        <div className="flex items-center gap-2">
          {p.pdf_base64 && (
            <button onClick={reDownload}
                    className="px-2 py-1 text-xs font-medium text-violet-700 bg-white hover:bg-violet-100 border border-violet-300 rounded inline-flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </button>
          )}
          <button onClick={onClose} className="text-violet-700 hover:text-violet-900 text-xs">Dismiss</button>
        </div>
      </div>
      <div className="text-xs text-violet-700 mb-3">Audience: {p.audience}</div>
      {p.executive_summary && (
        <p className="text-sm mb-3"><strong>Executive summary: </strong>{p.executive_summary}</p>
      )}
      {(p.sections || []).slice(0, 6).map((s, i) => (
        <div key={i} className="mb-2">
          <h4 className="font-semibold text-sm text-violet-900">{s.heading}</h4>
          <p className="text-xs text-gray-700 whitespace-pre-line">{s.body}</p>
        </div>
      ))}
      {(p.recommendations || []).length > 0 && (
        <div className="mt-2 text-sm">
          <strong>Recommendations:</strong>
          <ul className="list-disc list-inside text-gray-700 text-xs">
            {p.recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {(p.compliance_notes || []).length > 0 && (
        <div className="mt-2 text-sm">
          <strong>Compliance notes:</strong>
          <ul className="list-disc list-inside text-gray-700 text-xs">
            {p.compliance_notes.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReportSummaryView({ report_data }) {
  if (!report_data) return <div className="text-sm text-gray-500">No data</div>;
  const totals = report_data.totals || {};
  return (
    <div className="text-sm space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-2 bg-white rounded border border-gray-200">
          <div className="text-xs text-gray-500">Total tCO2e</div>
          <div className="text-xl font-bold text-emerald-700">{Number(totals.total_co2e_tonnes || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-white rounded border border-gray-200">
          <div className="text-xs text-gray-500">Rows aggregated</div>
          <div className="text-xl font-bold text-gray-900">{totals.row_count || 0}</div>
        </div>
      </div>
      {report_data.by_scope?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1">By scope</div>
          {report_data.by_scope.map((s, i) => (
            <div key={i} className="flex justify-between text-xs py-0.5">
              <span>{s.scope || '(unscoped)'}</span>
              <span className="font-mono">{Number(s.co2e_tonnes).toFixed(2)} tCO2e</span>
            </div>
          ))}
        </div>
      )}
      {report_data.by_facility?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1">By facility</div>
          {report_data.by_facility.slice(0, 8).map((s, i) => (
            <div key={i} className="flex justify-between text-xs py-0.5">
              <span className="text-gray-700">{s.site_name || s.site_id}</span>
              <span className="font-mono">{Number(s.co2e_kg / 1000).toFixed(2)} tCO2e</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
